import { RouteLocation, RouteLocationNamedRaw, RouteLocationPathRaw } from 'vue-router'
import { UserQuickFilter, UserTab } from '@/interfaces/user-management/Users.dao'

/** A link that hands a toast to the page it opens; that page shows it once (`takeHistoryToast`). */
export const withToast = (link: RouteLocationNamedRaw, toast: string): RouteLocationNamedRaw => ({
    ...link,
    state: { toast },
})

/** The filters the Users list reads from its URL when another page opens it. */
export interface UsersListLink {
    tab?: UserTab
    quick?: UserQuickFilter
    domain?: string
}

/** The Users list with the given tab and filters in the URL. */
export const usersListLink = (link: UsersListLink): RouteLocationNamedRaw => ({
    name: 'admin.users.users',
    query: Object.fromEntries(Object.entries(link).filter(([, value]) => value)),
})

/** The tabs of the user page. */
export type UserPageTab = 'access' | 'activity' | 'history'

/** The Groups list. */
export const groupsLink = (): RouteLocationNamedRaw => ({ name: 'admin.users.groups' })

/** The tabs of the group page. */
export type GroupPageTab = 'permissions' | 'members' | 'history'

/** One group's page. */
export const groupLink = (id: number | string): RouteLocationNamedRaw => ({
    name: 'admin.users.group',
    params: { id: String(id) },
})

/** One user's page, on a tab when given. */
export const userLink = (id: number | string, tab?: UserPageTab): RouteLocationNamedRaw => ({
    name: 'admin.users.user',
    params: { id: String(id) },
    ...(tab ? { query: { tab } } : {}),
})

/**
 * The page an old `/admin/user-management` URL with no page of its own opens - home, configuration,
 * the create forms: the CO Core menu, bookmarks and sent emails still carry those URLs. The old
 * query is dropped, its keys mean nothing to these pages.
 */
export const legacyUserManagementTarget = (to: RouteLocation): RouteLocationNamedRaw => {
    const [area, id] = ([] as string[]).concat(to.params.legacy ?? [])
    const detail = /^\d+$/.test(id ?? '')
    const page = (link: RouteLocationNamedRaw): RouteLocationNamedRaw => ({ params: {}, ...link, query: {}, hash: '' })
    if (area === 'users') return page(detail ? userLink(id) : usersListLink({}))
    if (area === 'groups') return page(detail ? groupLink(id) : groupsLink())
    return page({ name: 'admin.users.overview' })
}

/**
 * The page a `/admin/users` URL opens: these pages lived there until they took the User Management
 * URLs, and links from that time keep their query and hash.
 */
export const formerUsersPathTarget = (to: RouteLocation): RouteLocationPathRaw => ({
    path: to.path.replace(/^\/admin\/users/, '/admin/user-management'),
})
